import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { doc, getDocFromServer } from "firebase/firestore";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updateProfile,
  updatePassword,
  sendEmailVerification,
  deleteUser,
  EmailAuthProvider,
  reauthenticateWithCredential,
  onAuthStateChanged,
  signInWithPopup,
  reauthenticateWithPopup,
  linkWithCredential,
  linkWithPopup,
  GoogleAuthProvider,
  fetchSignInMethodsForEmail
} from "firebase/auth";
import { auth, db, googleProvider, clearLocalCache } from "@/lib/firebase/firebase";
import { ROUTES } from "@/lib/navigation";
import { toast } from "sonner";
import { createOrUpdateUser, isAdminUser, updateUserDisplayName } from "@/lib/firebase/services";
import { composeFullName, type PersonName } from "@/lib/personName";
import { OWN_ADMIN_ACCOUNT_DELETE_BLOCKED } from "@/lib/accountDeletion";
import { clearUsersCache } from "@/lib/hooks/useUsers";
import { deleteProfilePhoto, uploadProfilePhoto } from "@/lib/firebase/profilePhoto";

/** A bejelentkezett fiókhoz tartozik-e Google-fiók (a profilkép onnan frissíthető). */
const hasGoogleProvider = (firebaseUser: { providerData: { providerId: string }[] }) =>
  firebaseUser.providerData.some(({ providerId }) => providerId === GoogleAuthProvider.PROVIDER_ID);

/** Tartozik-e jelszó a fiókhoz (Google-lel regisztrálva nincs, amíg be nem állítja). */
const hasPasswordProvider = (firebaseUser: { providerData: { providerId: string }[] }) =>
  firebaseUser.providerData.some(({ providerId }) => providerId === EmailAuthProvider.PROVIDER_ID);

/**
 * A fiók saját címére hangolt Google-provider. A `login_hint` csak ELŐVÁLASZTJA a fiókot -
 * a `select_account` miatt a választó továbbra is megjelenik, tehát más fiók is
 * kiválasztható. A `reauthenticateWithPopup` az eltérést `auth/user-mismatch`-csel
 * elutasítja, a `linkWithPopup` viszont NEM: ott a rossz fiók összekapcsolása sikerülne,
 * ezért mondja ki a siker-üzenet, melyik cím lett összekapcsolva.
 */
const createGoogleProvider = (email: string | null) => {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({
    prompt: "select_account",
    ...(email ? { login_hint: email } : {}),
  });
  return provider;
};

/** A Firebase hibakódja (`auth/...`), ha az elkapott érték egyáltalán hordoz ilyet. */
const getAuthErrorCode = (error: unknown): string | undefined =>
  error && typeof error === "object" && "code" in error ? String(error.code) : undefined;

/**
 * A LETILTOTT felugró ablak más, mint a bezárt: a felhasználó nem is látta, és amíg nem
 * engedélyezi, minden újrapróbálkozás ugyanígy elbukik - ezt ki kell mondani neki.
 */
const POPUP_BLOCKED_MESSAGE =
  "A böngésző letiltotta a felugró ablakot. Engedélyezd az oldalnak, és próbáld újra.";

const isPopupBlocked = (code: string | undefined) => code === "auth/popup-blocked";

/** A jelszóművelet ismert hibái. Ami nincs benne, arra általános üzenet megy ki. */
const PASSWORD_ERROR_MESSAGES: Record<string, string> = {
  "auth/invalid-credential": "Hibás jelszó. Ellenőrizd és próbáld újra.",
  "auth/wrong-password": "Hibás jelszó. Ellenőrizd és próbáld újra.",
  "auth/weak-password": "Ez a jelszó túl gyenge. Válassz hosszabbat.",
  "auth/too-many-requests": "Túl sok próbálkozás. Várj pár percet, és próbáld újra.",
  "auth/user-mismatch": "Ez a Google-fiók egy másik felhasználóhoz tartozik.",
  "auth/credential-already-in-use": "Ez a Google-fiók egy másik felhasználóhoz tartozik.",
  "auth/popup-blocked": POPUP_BLOCKED_MESSAGE,
  // A helyi állapot elavult: a fiókhoz közben (másik lapon) jelszó került. Az újrapróbálás
  // sosem sikerülne, ezért az üzenet nem azt kéri.
  "auth/provider-already-linked":
    "Ehhez a fiókhoz már tartozik jelszó. Töltsd újra az oldalt, és a meglévőt módosítsd.",
};

/** A felugró ablak bezárása nem hiba: a felhasználó meggondolta magát. */
const isPopupCancelled = (code: string | undefined) =>
  code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request";

/**
 * A bejelentkezett Firebase-felhasználó, vagy egy KIÍRT hiba. A fiókműveletek mind ezzel
 * indulnak: egy nyers `throw` a `try` előtt némán bukna el, mert a hívók a hibaüzenetet a
 * kontextusra bízzák (lejárt munkamenet mellett a gomb csak nem csinálna semmit).
 */
const requireCurrentUser = () => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    toast.error("A munkameneted lejárt. Jelentkezz be újra.");
    throw new Error("Nincs bejelentkezett felhasználó");
  }
  return currentUser;
};

/** Az összekapcsolt Google-fiók címe (a siker-üzenet ezzel mondja meg, melyik lett az). */
const getGoogleProviderEmail = (firebaseUser: { providerData: { providerId: string; email: string | null }[] }) =>
  firebaseUser.providerData.find(({ providerId }) => providerId === GoogleAuthProvider.PROVIDER_ID)
    ?.email ?? null;

/** A Google-fiók profilképe az Auth rekordból (a Google minden belépéskor frissíti). */
const getGooglePhotoURL = (firebaseUser: { providerData: { providerId: string; photoURL: string | null }[] }) =>
  firebaseUser.providerData.find(({ providerId }) => providerId === GoogleAuthProvider.PROVIDER_ID)
    ?.photoURL ?? null;

/**
 * A munkamenet vége EZEN az eszközön: a helyi Firestore cache törlése, majd a főoldal teljes
 * újratöltéssel. A kijelentkezés és a saját fiók törlése is ezzel zár.
 *
 * A `persistentLocalCache` IndexedDB-ben tartja, amit az alkalmazás olvasott (a profil, a
 * foglalások, adminnál a listák), és közös gépen ez a munkamenet után is ott maradna; a
 * törölt fiók adatai pedig különösen nem. A cache törlése után a példány nem használható,
 * ezért újratöltés, nem sima navigáció. (A `clearLocalCache` maga nem dob.)
 */
const clearDeviceAndReload = async () => {
  await clearLocalCache();
  window.location.replace(ROUTES.home);
};

interface AuthContextType {
  user: {
    uid: string;
    email: string | null;
    displayName?: string | null;
    photoURL?: string | null;
    isAdmin?: boolean;
    emailVerified?: boolean;
    /**
     * Van-e Google-fiók a belépéshez. A profilkép Google-ből frissítése ettől
     * FÜGGETLENÜL felkínálható (enélkül a kapcsolat is létrejön), de a művelet
     * leírása ez alapján mondja meg, mi fog történni.
     */
    hasGoogleAccount?: boolean;
    /**
     * Van-e jelszava a fióknak. Ettől függ, hogy a jelszó-dialógus MÓDOSÍT (a jelenlegi
     * jelszóval azonosítva) vagy BEÁLLÍT (Google-ablakkal azonosítva), és hogy a fiók
     * törlése kér-e jelszót.
     */
    hasPasswordAccount?: boolean;
  } | null;
  loading: boolean;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, name: PersonName) => Promise<void>;
  signInWithGoogle: () => Promise<{ needsLinking: boolean; email?: string; pendingCredential?: ReturnType<typeof GoogleAuthProvider.credentialFromError> }>;
  linkGoogleAccount: (email: string, password: string, pendingCredential: ReturnType<typeof GoogleAuthProvider.credentialFromError>) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  deleteAccount: (password?: string) => Promise<void>;
  updateProfilePicture: (imageFile: File, onProgress?: (percent: number) => void) => Promise<void>;
  removeProfilePicture: () => Promise<void>;
  refreshGooglePhoto: () => Promise<void>;
  updateDisplayName: (name: PersonName) => Promise<void>;
  /** `currentPassword` nélkül: a fiókhoz még nincs jelszó, az azonosítás a Google-ablak. */
  changePassword: (newPassword: string, currentPassword?: string) => Promise<void>;
  connectGoogleAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signInWithEmail: async () => {},
  signUpWithEmail: async () => {},
  signInWithGoogle: async () => ({ needsLinking: false }),
  linkGoogleAccount: async () => {},
  resetPassword: async () => {},
  signOut: async () => {},
  deleteAccount: async () => {},
  updateProfilePicture: async () => {},
  removeProfilePicture: async () => {},
  refreshGooglePhoto: async () => {},
  updateDisplayName: async () => {},
  changePassword: async () => {},
  connectGoogleAccount: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthContextType["user"]>(null);
  const [loading, setLoading] = useState(true);
  /**
   * A belépés-figyelő profil-létrehozása (`createOrUpdateUser`), a fiók uid-jával. Sosem
   * dob (a hibáját a figyelő elnyeli); a regisztráció megvárja, mielőtt a nevet írná.
   */
  const profileCreationRef = useRef<{ uid: string; done: Promise<void> } | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        // A felhasználólista gyorstára modul szintű, tehát túlélné a munkamenetet, ha a
        // kijelentkezés nem járna oldal-újratöltéssel (lejáró jogosultság, másik lap)
        clearUsersCache();
        setUser(null);
        setLoading(false);
        return;
      }

      // A profil-dokumentum biztosítása és az admin jogosultság lekérése független egymástól,
      // ezért párhuzamosan futnak: a belépő így egy Firestore-kör után kész, nem több egymás
      // utáni után. A profil mentése nem befolyásolja a jogosultságot, a hibája nem állít le.
      const profileCreation = createOrUpdateUser({
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
      }).catch((error) => console.error("Hiba a felhasználói profil mentésekor:", error));
      profileCreationRef.current = { uid: firebaseUser.uid, done: profileCreation };
      const [isAdmin] = await Promise.all([isAdminUser(firebaseUser.uid), profileCreation]);

      // Egy lépésben, a jogosultsággal együtt: a fejléc nem villan előbb "sima" felhasználóként
      setUser({
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        isAdmin,
        emailVerified: firebaseUser.emailVerified,
        hasGoogleAccount: hasGoogleProvider(firebaseUser),
        hasPasswordAccount: hasPasswordProvider(firebaseUser),
      });
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signInWithEmail = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error: unknown) {
      console.error("Error signing in with email:", error);
      if (error instanceof Error && 'code' in error && error.code === 'auth/invalid-credential') {
        throw new Error("Hibás email vagy jelszó");
      }
      throw error;
    }
  };

  const signInWithGoogle = async (): Promise<{ needsLinking: boolean; email?: string; pendingCredential?: ReturnType<typeof GoogleAuthProvider.credentialFromError> }> => {
    try {
      await signInWithPopup(auth, googleProvider);

      // Sikeres bejelentkezés - Firestore user létrehozása/frissítése az onAuthStateChanged-ben történik
      toast.success("Sikeres bejelentkezés Google fiókkal!");
      return { needsLinking: false };
    } catch (error: unknown) {
      console.error("Error signing in with Google:", error);

      // Ha már létezik fiók ezzel az email címmel más provider-rel
      if (error && typeof error === 'object' && 'code' in error &&
          error.code === 'auth/account-exists-with-different-credential') {
        const authError = error as { code: string; customData?: { email?: string } };
        const email = authError.customData?.email;
        const pendingCredential = GoogleAuthProvider.credentialFromError(error as Parameters<typeof GoogleAuthProvider.credentialFromError>[0]);

        if (email) {
          // Ellenőrizzük milyen bejelentkezési módszerek vannak ehhez az emailhez
          const signInMethods = await fetchSignInMethodsForEmail(auth, email);

          if (signInMethods.includes('password')) {
            // Email+jelszó fiók létezik - kérjük a jelszót a linkeléshez
            return { needsLinking: true, email, pendingCredential };
          }
        }

        throw new Error("Ez az email cím már használatban van egy másik bejelentkezési móddal.");
      }

      // Popup bezárva
      if (error && typeof error === 'object' && 'code' in error && error.code === 'auth/popup-closed-by-user') {
        throw new Error("A bejelentkezési ablak bezárásra került.");
      }

      throw new Error("Hiba történt a Google bejelentkezés során.");
    }
  };

  const linkGoogleAccount = async (
    email: string,
    password: string,
    pendingCredential: ReturnType<typeof GoogleAuthProvider.credentialFromError>
  ) => {
    try {
      if (!pendingCredential) {
        throw new Error("Hiányzó Google hitelesítési adat");
      }

      // Bejelentkezés email+jelszóval
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Google fiók összekapcsolása
      await linkWithCredential(userCredential.user, pendingCredential);

      toast.success("Google fiók sikeresen összekapcsolva!", {
        description: "Mostantól Google-lal is bejelentkezhetsz."
      });
    } catch (error: unknown) {
      console.error("Error linking Google account:", error);
      if (error instanceof Error && 'code' in error && error.code === 'auth/invalid-credential') {
        throw new Error("Hibás jelszó");
      }
      throw error;
    }
  };

  const signUpWithEmail = async (email: string, password: string, name: PersonName) => {
    // A fiók neve magyar sorrendben („Kovács Anna”), a két rész a `users` dokumentumra is megy
    const displayName = composeFullName(name.lastName, name.firstName);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const newUser = userCredential.user;
      await updateProfile(newUser, { displayName });
      // A figyelő a fejléc állapotát is az üres névvel állíthatta be (az `updateProfile` nem
      // indítja újra); ha még nem ért oda, a már frissített Auth-felhasználóból olvas
      setUser((prevUser) => (prevUser?.uid === newUser.uid ? { ...prevUser, displayName } : prevUser));

      // A név a `users` dokumentumba is kell (a Felhasználók lista azt olvassa). A fiók
      // létrejötte már elindította a belépés-figyelőt, ami a dokumentumot a MÉG ÜRES Auth-névvel
      // hozza létre - a csak létrehozó `createOrUpdateUser` itt tehát semmit nem írna. Ezért a
      // célzott `updateUserDisplayName` írja, és csak a figyelő létrehozása UTÁN: ha a kettő
      // egyszerre hozná létre, a második írást a szabály elutasítaná (a `createdAt` változna).
      // Ez csak TÜKÖR, ezért nem várjuk meg: a fiók ekkor már létezik és be is van jelentkezve,
      // tehát egy elhasaló vagy beragadó Firestore-írás (a Firestore nem érhető el, az Auth igen)
      // nyers hibát vagy végtelen pörgést adna, megerősítő levél nélkül - az újrapróbálásra pedig
      // már az jönne, hogy az e-mail cím foglalt. A hibája a naplóba megy; a név a profilon
      // bármikor újra menthető.
      const listenerCreation = profileCreationRef.current;
      const listenerDone =
        listenerCreation?.uid === newUser.uid ? listenerCreation.done : Promise.resolve();
      void listenerDone
        .then(() => updateUserDisplayName({ uid: newUser.uid, email: newUser.email }, displayName, name))
        .catch((error) => console.error("Nem sikerült a nevet a felhasználói profilba menteni:", error));

      // Email megerősítés küldése alapértelmezett beállításokkal
      try {
        // Elküldjük a hivatalos Firebase email megerősítőt alapértelmezett beállításokkal
        await sendEmailVerification(userCredential.user);
        
        toast.success(
          "Regisztráció sikeres! Kérjük, ellenőrizd az email fiókodat és erősítsd meg az email címedet.",
          {
            duration: 6000,
            description: "Az email címed megerősítéséhez elküldtük a szükséges linket."
          }
        );
      } catch (verificationError) {
        console.error('Hiba az email megerősítő küldése közben:', verificationError);
        
        // Specifikus hibakezelés
        let errorMessage = "Hiba történt az email megerősítő küldése során.";
        
        if (verificationError && typeof verificationError === 'object' && 'code' in verificationError) {
          switch (verificationError.code) {
            case 'auth/too-many-requests':
              errorMessage = "Túl sok kérés. Kérjük, próbáld újra később.";
              break;
            case 'auth/invalid-email':
              errorMessage = "Érvénytelen email cím.";
              break;
            case 'auth/user-not-found':
              errorMessage = "A felhasználó nem található.";
              break;
            default:
              errorMessage = `Email küldési hiba: ${verificationError.code}`;
          }
        }
        
        toast.error("Email megerősítő küldése sikertelen", {
          description: errorMessage,
          duration: 8000
        });
      }
    } catch (error: unknown) {
      console.error("Error signing up with email:", error);
      if (error instanceof Error && 'code' in error && error.code === 'auth/email-already-in-use') {
        throw new Error("Ez az email cím már használatban van");
      }
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success("Jelszó-visszaállítási email elküldve");
    } catch (error) {
      console.error("Error resetting password:", error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      await clearDeviceAndReload();
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    }
  };

  const deleteAccount = async (password?: string) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Nincs bejelentkezett felhasználó");
      }

      /*
        Admin fiók innen nem törölhető: az `adminUsers/{uid}` sorát a böngésző nem írhatja
        (a szabály `if false`), és a szerver takarítása sem törli, tehát egy Auth-fiók nélküli
        admin sor maradna. A jogot előbb egy másik adminnak kell elvennie a Felhasználók
        listában. A profil oldal ilyenkor fel sem kínálja a törlést; ez az őr a friss
        állapotot kérdezi (a jog a belépés óta is születhetett), az azonosító ablak előtt.

        ZÁRTAN bukik: közvetlenül a szervertől olvas (nem az `isAdminUser`, ami a hibát
        „nem admin"-nak veszi, és nem a helyi gyorsítótár), és ha a válasz nem jön meg,
        semmit nem töröl.
      */
      let isAdmin: boolean;
      try {
        isAdmin = (await getDocFromServer(doc(db, "adminUsers", currentUser.uid))).exists();
      } catch (error) {
        console.error("Hiba az admin jogosultság ellenőrzésekor:", error);
        throw new Error(
          "Nem sikerült ellenőrizni a fiókodat, ezért nem töröltünk semmit. Ellenőrizd a kapcsolatot, és próbáld újra.",
        );
      }
      if (isAdmin) {
        throw new Error(OWN_ADMIN_ACCOUNT_DELETE_BLOCKED);
      }

      // A törlés előtt friss azonosítás kell (`auth/requires-recent-login`). Jelszavas
      // fióknál ezt a beírt jelszó adja; jelszó nélkülinél - Google-lel regisztrált
      // vendég - a Google-ablak, különben a fiókját soha nem tudná törölni.
      try {
        if (password && currentUser.email) {
          await reauthenticateWithCredential(
            currentUser,
            EmailAuthProvider.credential(currentUser.email, password),
          );
        } else if (!password && hasGoogleProvider(currentUser)) {
          await reauthenticateWithPopup(currentUser, createGoogleProvider(currentUser.email));
        }
      } catch (authError: unknown) {
        const code = getAuthErrorCode(authError);
        console.error("Újrahitelesítési hiba:", authError);
        if (isPopupCancelled(code)) {
          throw new Error("A törlés megszakadt: az azonosító ablak bezárult.");
        }
        if (isPopupBlocked(code)) {
          throw new Error(POPUP_BLOCKED_MESSAGE);
        }
        if (code === 'auth/invalid-credential' || code === 'auth/wrong-password') {
          throw new Error("Hibás jelszó. Kérjük, ellenőrizd és próbáld újra.");
        }
        throw authError;
      }

      /*
        CSAK az Auth-fiókot töröljük. A profilt (`users/{uid}`) és a profilképet a szerver
        takarítja UTÁNA (`onAuthUserDeleted`, functions/src/users.js), és egy sírkővel meg is
        akadályozza, hogy egy még élő token visszahozza a profilt.

        Korábban a böngésző ELŐBB a profilt és a képet törölte, és ha utána a `deleteUser`
        elbukott (például `auth/requires-recent-login`), élő fiók maradt profil nélkül, amit a
        következő belépés újra létrehozott. A böngésző ezt a sorrendet nem tudja megfordítani:
        a törlés után már se a profilját, se a képét nem éri el. A `users` dokumentum
        törlését a szabály (`allow delete: if false`) azóta nem is engedi.
      */
      await deleteUser(currentUser);
      // Ugyanaz a zárás, mint a kijelentkezésé: a törölt vendég profilja és foglalásai ne
      // maradjanak a gép helyi cache-ében. Visszajelzés nincs (a kijelentkezésnek sincs):
      // az újratöltés elvinné, és a főoldal kijelentkezve maga a válasz.
      await clearDeviceAndReload();
    } catch (error: unknown) {
      console.error("Error deleting account:", error);
      throw error;
    }
  };

  /**
   * A kép ELŐZETESEN átment a kép-igazítón (`ImageCropDialog`), tehát mindig
   * négyzetes, 512 px-es JPEG érkezik ide: méret- és formátumellenőrzés ezért
   * nincs, és a felhasználónak sincs mit betartania.
   */
  const updateProfilePicture = async (imageFile: File, onProgress?: (percent: number) => void) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Nincs bejelentkezett felhasználó");
      }

      const downloadURL = await uploadProfilePhoto(currentUser.uid, imageFile, onProgress);

      // Frissítjük a Firebase Auth profilt
      await updateProfile(currentUser, { photoURL: downloadURL });

      // Frissítjük a lokális felhasználó állapotot
      setUser(prevUser => prevUser ? {
        ...prevUser,
        photoURL: downloadURL,
      } : null);

      toast.success("Profilkép sikeresen frissítve");
    } catch (error: unknown) {
      console.error("Error updating profile picture:", error);
      toast.error(error instanceof Error ? error.message : "Hiba történt a profilkép frissítésekor");
      throw error;
    }
  };

  const removeProfilePicture = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Nincs bejelentkezett felhasználó");
      }

      await deleteProfilePhoto(currentUser.uid);
      await updateProfile(currentUser, { photoURL: null });

      setUser(prevUser => prevUser ? {
        ...prevUser,
        photoURL: null,
      } : null);

      toast.success("Profilkép sikeresen törölve");
    } catch (error: unknown) {
      console.error("Error removing profile picture:", error);
      toast.error(error instanceof Error ? error.message : "Hiba történt a profilkép törlésekor");
      throw error;
    }
  };

  /**
   * A profilkép újrakérése a Google-fiókból.
   *
   * A Firebase a Google profiladatait (nevet, képet) CSAK a Google-lel való
   * hitelesítéskor frissíti, ezért egy "beragadt" régi képet önmagában sem az
   * újratöltés, sem az újbóli bejelentkezés nem hoz helyre - kell egy új
   * Google-hitelesítés. Ezt kéri a felhasználó a gombbal, felugró ablakban.
   *
   * Két eset van, és a felugró ablakot NEM ugyanaz a hívás nyitja:
   * - a fiókhoz már tartozik Google-provider -> `reauthenticateWithPopup`, a fiók
   *   beállításai nem változnak;
   * - még nem tartozik (jelszavas fiók, amiben csak a RÉGI Google-kép ragadt bent)
   *   -> `linkWithPopup`, mert a Google-profilhoz máshogy nem jutunk hozzá. Ezzel a
   *   fiók a Google-bejelentkezést is megkapja - a művelet leírása ezt kimondja.
   *
   * A `login_hint` a fiók saját e-mail-címét jelöli ki előre, hogy a felhasználó ne
   * kapcsoljon be véletlenül egy másik Google-fiókot.
   */
  const refreshGooglePhoto = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("Nincs bejelentkezett felhasználó");
    }

    const alreadyLinked = hasGoogleProvider(currentUser);
    const provider = createGoogleProvider(currentUser.email);

    try {
      if (alreadyLinked) {
        await reauthenticateWithPopup(currentUser, provider);
      } else {
        await linkWithPopup(currentUser, provider);
      }
      // A hitelesítés a szerveren frissíti a tárolt Google-profilt; a helyi
      // rekordba ezt a `reload` hozza be.
      await currentUser.reload();

      // Az összekapcsolás ITT MÁR megtörtént, és nem vonható vissza: a helyi állapotnak
      // akkor is ezt kell mutatnia, ha a kép beolvasása alább elbukik - különben a
      // művelet leírása továbbra is összekapcsolást ígérne egy már összekapcsolt fióknak.
      if (!alreadyLinked) {
        setUser(prevUser => prevUser ? { ...prevUser, hasGoogleAccount: true } : null);
      }

      const googlePhotoURL = getGooglePhotoURL(currentUser);
      if (!googlePhotoURL) {
        throw new Error(
          alreadyLinked
            ? "A Google-fiókodban nincs profilkép."
            : "A Google-fiókod össze van kapcsolva, de nincs benne profilkép."
        );
      }

      await updateProfile(currentUser, { photoURL: googlePhotoURL });

      setUser(prevUser => prevUser ? {
        ...prevUser,
        photoURL: googlePhotoURL,
      } : null);

      // A korábban feltöltött fájl innentől sehol nem látszik: törlés nélkül
      // örökre a tárolóban maradna. A hiba itt már nem állítja meg a műveletet,
      // a felhasználó szempontjából a kép cseréje sikerült.
      await deleteProfilePhoto(currentUser.uid).catch((error) =>
        console.error("Hiba a korábbi profilkép törlésekor:", error)
      );

      toast.success("Profilkép frissítve a Google-fiókodból");
    } catch (error: unknown) {
      const code = getAuthErrorCode(error);

      if (isPopupCancelled(code)) return;

      console.error("Error refreshing Google profile picture:", error);

      // A választott Google-fiók egy MÁSIK itteni fiókhoz tartozik (`credential-already-in-use`),
      // vagy nem ehhez a felhasználóhoz (`user-mismatch`): a kép nem hozható át, és a
      // felhasználónak tudnia kell, hogy melyik fiókot kell választania.
      if (code === "auth/credential-already-in-use" || code === "auth/user-mismatch") {
        toast.error("Ez a Google-fiók egy másik felhasználóhoz tartozik.", {
          description: currentUser.email
            ? `Válaszd azt a Google-fiókot, amelyik a(z) ${currentUser.email} címhez tartozik.`
            : "Válaszd azt a Google-fiókot, amelyik ehhez a fiókhoz tartozik.",
        });
      } else {
        toast.error(
          error instanceof Error && !code
            ? error.message
            : "Nem sikerült lekérni a képet a Google-fiókodból. Próbáld újra."
        );
      }

      throw error;
    }
  };

  /**
   * A megjelenítendő név átírása. Két helyre kell: az Auth rekordba (ezt olvassa a fejléc
   * és a foglalási űrlap előkitöltése) és a `users` dokumentumba (ezt listázza az admin).
   * A kettő EGYÜTT megy: ha az egyik írás elbukik, a hiba felszínre jön, mert a két forrás
   * szétcsúszása pont az a némán elromló állapot, amit a felhasználó nem lát - a saját neve
   * mindenhol frissnek látszana, az admin listájában viszont a régi maradna.
   */
  const updateDisplayName = async (name: PersonName) => {
    const currentUser = requireCurrentUser();
    // A teljes név magyar sorrendben; a két rész a `users` dokumentumra is megy, hogy a
    // foglalási űrlap ne tippelni kényszerüljön
    const displayName = composeFullName(name.lastName, name.firstName);

    try {
      // A Firestore megy ELŐBB: ha ez bukik, még semmi nem változott, és a hibaüzenet
      // igazat mond. Fordítva a felhasználó hibát kapna egy olyan névre, amit az Auth
      // rekord már hordoz - a fejlécében a következő újratöltéskor meg is jelenne.
      await updateUserDisplayName(
        { uid: currentUser.uid, email: currentUser.email },
        displayName,
        name,
      );
      await updateProfile(currentUser, { displayName });

      setUser(prevUser => prevUser ? { ...prevUser, displayName } : null);
      toast.success("A neved frissítve");
    } catch (error: unknown) {
      console.error("Error updating display name:", error);
      toast.error("Nem sikerült frissíteni a nevedet. Próbáld újra.");
      throw error;
    }
  };

  /**
   * Jelszó módosítása vagy - Google-lel belépő fióknál - beállítása.
   *
   * Mindkét ág FRISS azonosítást kér, mert a jelszó megszerzése a fiók teljes átvételét
   * jelentené: jelszavas fióknál a jelenlegi jelszó (`reauthenticateWithCredential`),
   * jelszó nélkülinél a Google-ablak (`reauthenticateWithPopup`). A Firebase amúgy is
   * `auth/requires-recent-login`-nal állna meg egy régi munkamenetnél - így viszont a
   * felhasználó nem egy érthetetlen hibába fut, hanem elvégzi az azonosítást.
   */
  const changePassword = async (newPassword: string, currentPassword?: string) => {
    const currentUser = requireCurrentUser();
    if (!currentUser.email) {
      toast.error("A fiókhoz nem tartozik e-mail cím, ezért nem állítható be jelszó.");
      throw new Error("A fiókhoz nem tartozik e-mail cím");
    }

    try {
      if (currentPassword) {
        await reauthenticateWithCredential(
          currentUser,
          EmailAuthProvider.credential(currentUser.email, currentPassword),
        );
        await updatePassword(currentUser, newPassword);
      } else {
        await reauthenticateWithPopup(currentUser, createGoogleProvider(currentUser.email));
        await linkWithCredential(
          currentUser,
          EmailAuthProvider.credential(currentUser.email, newPassword),
        );
        setUser(prevUser => prevUser ? { ...prevUser, hasPasswordAccount: true } : null);
      }

      toast.success(
        currentPassword ? "A jelszavad frissítve" : "A jelszavad beállítva",
        { description: "Mostantól ezzel a jelszóval tudsz belépni." },
      );
    } catch (error: unknown) {
      const code = getAuthErrorCode(error);

      // A megszakított azonosító ablak itt NEM csendes visszatérés (mint a profilkép
      // frissítésénél): a felhasználó már beírta az új jelszót, és a hívó dialógus a
      // sikeres lefutásra zárna - a jelszó viszont nem változott volna meg.
      if (isPopupCancelled(code)) {
        toast.error("A művelet megszakadt: az azonosító ablak bezárult.");
        throw error;
      }

      console.error("Error changing password:", error);
      toast.error(PASSWORD_ERROR_MESSAGES[code ?? ""] ?? "Nem sikerült módosítani a jelszavadat. Próbáld újra.", {
        description:
          code === "auth/user-mismatch" || code === "auth/credential-already-in-use"
            ? `Válaszd azt a Google-fiókot, amelyik a(z) ${currentUser.email} címhez tartozik.`
            : undefined,
      });
      throw error;
    }
  };

  /**
   * A Google-fiók összekapcsolása egy jelszavas fiókkal: innentől mindkét módon be lehet
   * lépni. Szétkapcsolás szándékosan nincs: egyetlen belépési mód maradna, és egy elfelejtett
   * jelszó azonnal kizárná a felhasználót a saját foglalásaiból.
   */
  const connectGoogleAccount = async () => {
    const currentUser = requireCurrentUser();

    try {
      await linkWithPopup(currentUser, createGoogleProvider(currentUser.email));
      await currentUser.reload();
      setUser(prevUser => prevUser ? { ...prevUser, hasGoogleAccount: true } : null);

      // A `linkWithPopup` NEM ellenőrzi, hogy a választott Google-fiók a miénk-e, és a
      // kapcsolat utólag nem bontható: a siker-üzenet ezért kimondja a címet, hogy egy
      // véletlenül választott (pl. munkahelyi) fiók azonnal kiderüljön.
      const linkedEmail = getGoogleProviderEmail(currentUser);
      toast.success("Google-fiók összekapcsolva", {
        description: linkedEmail
          ? `Mostantól a(z) ${linkedEmail} Google-fiókkal is beléphetsz.`
          : "Mostantól Google-lal is beléphetsz.",
      });
    } catch (error: unknown) {
      const code = getAuthErrorCode(error);

      if (isPopupCancelled(code)) return;

      console.error("Error connecting Google account:", error);

      if (isPopupBlocked(code)) {
        toast.error(POPUP_BLOCKED_MESSAGE);
      } else if (code === "auth/credential-already-in-use" || code === "auth/user-mismatch") {
        // A választott Google-fiók egy MÁSIK itteni fiókhoz tartozik: a felhasználónak
        // tudnia kell, melyiket kell választania.
        toast.error("Ez a Google-fiók egy másik felhasználóhoz tartozik.", {
          description: currentUser.email
            ? `Válaszd azt a Google-fiókot, amelyik a(z) ${currentUser.email} címhez tartozik.`
            : "Válaszd azt a Google-fiókot, amelyik ehhez a fiókhoz tartozik.",
        });
      } else {
        toast.error("Nem sikerült összekapcsolni a Google-fiókodat. Próbáld újra.");
      }
      throw error;
    }
  };

  const value = {
    user,
    loading,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    linkGoogleAccount,
    resetPassword,
    signOut,
    deleteAccount,
    updateProfilePicture,
    removeProfilePicture,
    refreshGooglePhoto,
    updateDisplayName,
    changePassword,
    connectGoogleAccount,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  return useContext(AuthContext);
} 